import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import { formatDate } from '#common/utils';
import { tagKey } from '#common/tags';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { useGetTagsQuery, useDeleteTagMutation } from '#services/tag';
import { useInitialLoadOverlay, useQueryErrorSnackbar, type TagRow } from './utils';

// getTags returns every tag at once, so filter/sort/paging happen client-side.
// ponytail: fine for hundreds of tags; add server paging if it reaches thousands.
export default function TagList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;
  const navigate = useNavigate();
  const query = useGetTagsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [deleteTag] = useDeleteTagMutation();
  const [filter, setFilter] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(30);

  useInitialLoadOverlay(query.isLoading);
  useQueryErrorSnackbar(query);

  const tags = (query.data || []) as TagRow[];
  const filterKey = tagKey(filter);
  const sorted = tags
    .filter((tag) => tagKey(tag.name).includes(filterKey))
    .sort((a, b) => {
      let diff: number;
      if (orderBy === 'productCount') diff = a.productCount - b.productCount;
      else if (orderBy === 'updatedAt') diff = String(a.updatedAt ?? a.createdAt ?? '').localeCompare(String(b.updatedAt ?? b.createdAt ?? ''));
      else diff = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return order === 'asc' ? diff : -diff;
    });
  const rows = sorted.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const handleDelete = (tag: TagRow) => {
    setDialogData({
      title: 'Delete',
      text: `Are you sure you want to delete the tag "${tag.name}"?`,
      disagree: 'Cancel',
      agree: 'Delete',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        showBackdrop();
        void deleteTag({ id: tag.id }).then((res) => {
          simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Tag "${tag.name}" was successfully deleted.`);
        }).finally(() => {
          hideBackdrop();
        });
      },
    });
    showDialog();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Name',
    renderer: (row) => {
      const tag = row as unknown as TagRow;
      const url = `/management/tag/edit?tagId=${tag.id}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
        >
          {tag.name}
        </Link>
      );
    },
  }, {
    id: 'productCount',
    label: 'Products',
  }, {
    id: 'updatedAt',
    label: 'Updated',
    renderer: (row) => {
      const tag = row as unknown as TagRow;
      return formatDate(tag.updatedAt ?? tag.createdAt ?? '', '-');
    },
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const tag = row as unknown as TagRow;
      if (tag.productCount > 0) {
        return (
          <Tooltip title={`Used by ${tag.productCount} product${tag.productCount === 1 ? '' : 's'}. Rename it to an existing tag to merge.`}>
            <span>
              <Link component="button" className="disabled-link">
                Delete
              </Link>
            </span>
          </Tooltip>
        );
      }
      return (
        <Link component="button" onClick={() => handleDelete(tag)}>
          Delete
        </Link>
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'tags',
    headers: tableHeaders,
    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize: (size) => {
      setPageSize(size);
      setPageIndex(0);
    },
    rowCount: sorted.length,
    rows: rows as unknown as EnhancedTableRow[],
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Tags
      </Box>

      <Paper
        elevation={0}
        variant="outlined"
        square
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 400,
        }}
      >
        <SearchIcon sx={{ m: '10px' }} />
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Filter Tags"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPageIndex(0);
          }}
        />
      </Paper>

      {query.isSuccess && !tags.length ? (
        <Box data-testid="tag-empty" sx={{ p: 2, color: 'text.secondary' }}>
          No tags yet. Create one here, or add tags while editing a product or importing a CSV.
        </Box>
      ) : (
        <EnhancedTable {...tableProps} />
      )}
    </>
  );
}
